export interface WorkflowGuideTopic {
  readonly id: string;
  readonly title: string;
  readonly summary: string;
}

export interface WorkflowGuideDefinition {
  readonly term: string;
  readonly meaning: string;
}

export const WORKFLOW_CHAIN = [
  'Owner',
  'Command Center',
  'Planning / approval',
  'Workers',
  'Reviewer',
] as const;

export const WORKFLOW_TOPICS: readonly WorkflowGuideTopic[] = [
  {
    id: 'command-center',
    title: 'Command Center',
    summary: 'Start normal work here. Tell Brunel the goal or ask a question. This is the one global place for new work and plan changes.',
  },
  {
    id: 'planning-room',
    title: 'Planning Room and plan approval',
    summary: 'Brunel turns a work request into a plan you can read. Nothing in that plan runs until the required approval is given.',
  },
  {
    id: 'workers',
    title: 'Workers',
    summary: 'Workers are the AI roles that carry out assigned tasks. Each Worker keeps its own identity, assignment, status, and working surface.',
  },
  {
    id: 'reviewer',
    title: 'Reviewer',
    summary: 'The Reviewer checks finished work. When a Worker reports a run done, it reviews that result and its pull request once, for how it works and how it looks, and passes it or files one finding for you. It does not watch work in progress and does not start work.',
  },
  {
    id: 'routing-lock',
    title: 'Routing lock',
    summary: 'A routing lock tells Brunel to keep new work for a project on a particular Worker until you change the lock. It does not skip planning or approval.',
  },
  {
    id: 'direct-terminal',
    title: 'Worker Terminal intervention',
    summary: 'Use that Worker’s live Terminal for immediate low-level intervention on work already in progress. Normal conversation stays in Main Command, and new consequential work stays in the normal planning chain.',
  },
  {
    id: 'owner-attention',
    title: 'Owner Attention',
    summary: 'This is the short list of items that need your decision, approval, or input. It is for action you must take, not every event Brunel records.',
  },
  {
    id: 'engine-room',
    title: 'Results and Engine Room',
    summary: 'Results in the right drawer shows what just finished. In Engine Room, Live shows what is happening now; History shows finished work, reports, conversations, plans, audit evidence, and charts without starting new work.',
  },
] as const;

export const WORKFLOW_DEFINITIONS: readonly WorkflowGuideDefinition[] = [
  {
    term: 'Computer',
    meaning: 'The physical or virtual machine doing the computing.',
  },
  {
    term: 'Node',
    meaning: 'Brunel’s connection to a computer. A node lets Brunel see that machine and route approved work to it.',
  },
  {
    term: 'Worker',
    meaning: 'An AI role that receives an assignment. A Worker can run on a computer through its node.',
  },
  {
    term: 'Terminal',
    meaning: 'The Worker’s live command-line session. It is where the Worker interacts with tools on the computer.',
  },
  {
    term: 'Task',
    meaning: 'One specific piece of approved work assigned to a Worker.',
  },
] as const;

export const NORMAL_WORKFLOW = [
  'Tell the Command Center what you want done.',
  'Read the plan Brunel prepares when the request needs work to run.',
  'Approve, reject, or discuss the plan.',
  'Brunel routes approved work to a Worker. When the Worker reports done, the Reviewer checks the result once.',
  'Watch Owner Attention for anything that needs your decision while work is running.',
  'Read the outcome in Results (right drawer) or open Engine Room → History for more history and detail.',
] as const;

export const DIRECT_TERMINAL_GUIDANCE = {
  useWhen: [
    'You need to type directly into one Worker’s live terminal session.',
    'You need to interrupt or redirect the program running in that terminal immediately.',
    'You are troubleshooting that Worker’s current terminal session.',
  ],
  avoidWhen: [
    'You want a normal conversation or question answered by a Worker.',
    'You are starting a new piece of consequential work.',
    'You want to change the project goal or need a new plan or approval decision.',
  ],
  normalPath: 'Use Main Command for conversation and consequential work so the normal planning, supervision, and approval chain stays intact.',
} as const;
